import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { useWindowSize } from '../../hooks';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  titleStartIcon?: JSX.Element;
  header?: React.ReactNode;
  content?: React.ReactNode;
  actions?: React.ReactNode;
  subtitle?: string;
  disableOverflow?: boolean;
}

const customStyles: Record<string, SxProps<Theme>> = {
  modal: {
    '& .MuiDialogContent-root': {
      padding: '0px 28px',
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'column',
    },
    '& .MuiDialogActions-root': {
      padding: '15px 28px',
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.09)',
    },
    '& .MuiDialog-paperFullScreen': {
      borderRadius: '0px',
    },
  },
  disableOverflow: {
    '& .MuiDialogContent-root': {
      overflow: 'hidden',
    },
  },
  modalContainer: {
    '& .MuiDialog-paper': {
      borderRadius: '20px',
      padding: '20px 0px 0px 0px',
      maxWidth: '700px',
      display: 'flex',
      flexGrow: 1,
    },
  },
  header: {
    padding: '12px 0',
  },
  modalTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0px 28px',
  },
  modalSubtitle: {
    color: '#6E7178',
    marginTop: '5px',
  },
  actions: {
    '@media (max-width: 480px)': {
      '& > .MuiButton-root:last-child': {
        marginRight: '40px',
      },
    },
  },
  titleStartIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
};

export const Modal = ({
  open,
  onClose,
  title,
  titleStartIcon,
  header,
  content,
  actions,
  subtitle,
  disableOverflow,
}: ModalProps) => {
  const { width } = useWindowSize();

  return (
    <Dialog
      onClose={onClose}
      open={open}
      sx={
        {
          ...customStyles.modal,
          ...(disableOverflow ? customStyles.disableOverflow : {}),
          ...customStyles.modalContainer,
        } as SxProps<Theme>
      }
      fullScreen={width < 480}
    >
      <Box sx={customStyles.modalTitle}>
        <div>
          <Box sx={customStyles.titleStartIcon}>
            {titleStartIcon}
            <Typography
              color='textSecondary'
              variant='h1'
            >
              {title}
            </Typography>
          </Box>
          <Typography
            color='textSecondary'
            variant='h3'
            sx={customStyles.modalSubtitle}
          >
            {subtitle}
          </Typography>
        </div>
        <IconButton
          onClick={onClose}
          size='large'
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent>
        {header && <Box sx={customStyles.header}>{header}</Box>}
        {content && content}
      </DialogContent>
      {actions && <DialogActions sx={customStyles.actions}>{actions}</DialogActions>}
    </Dialog>
  );
};
